<template>
  <div v-show="isVisible" class="contact">
    <div class="contact__hero">
      <div class="contact__hero-title">
        {{ $t('heroTitle') }}
      </div>
    </div>
    <div class="contact__headline" v-html="resolveLineBreakOfLastWord($t('heroSubtitle'))" />
    <div class="contact__info-table-wrap">
      <div class="contact__info-table">
        <div class="contact__info-block">
          <IconPhoneCall class="contact__info-icon" />
          <h3 class="contact__info-heading">
            {{ $t('phone.heading') }}
          </h3>
          <p>{{ $t('phone.note') }}</p>
          <p class="contact__info-details">
            <span class="u-nobr">{{ $t('phone.govCanadaLabel') }}</span>
            {{ ' ' }}
            <span class="u-nobr">{{ $t('phone.govCanadaNumber') }}</span>
            <br />
            <span class="u-nobr">{{ $t('phone.govQuebecLabel') }}</span>
            {{ ' ' }}
            <span class="u-nobr">{{ $t('phone.govQuebecNumber') }}</span>
          </p>
          <p>{{ $t('phone.accesImpotLabel') }}</p>
          <p class="contact__info-details" @click="trackContactClick(TRACK_TARGETS.phone)">
            <span class="u-nobr">{{ $t('phone.quebecNumber') }}</span>
            <br />
            <span class="u-nobr">{{ $t('phone.tollFreeNumber') }}</span>
          </p>
          <p class="contact__info-details">
            <span class="u-nobr">{{ $t('phone.openingHours') }}</span>
            {{ ' ' }}
            <span class="u-nobr">{{ $t('phone.openingDays') }}</span>
          </p>
        </div>
        <div class="contact__info-block">
          <IconSpeechBubble class="contact__info-icon" />
          <h3 class="contact__info-heading">
            {{ $t('chat.heading') }}
          </h3>
          <p class="contact__info-details">
            <span class="u-nobr">{{ $t('chat.openingHours') }}</span>
            {{ ' ' }}
            <span class="u-nobr">{{ $t('chat.openingDays') }}</span>
          </p>
          <div class="contact__info-button-wrap">
            <Button
              class="contact__info-button"
              :size="BUTTON_STYLES.size.small"
              @click="openLiveChat"
            >
              {{ $t('chat.buttonText') }}
            </Button>
          </div>
        </div>
        <div class="contact__info-block">
          <IconMail class="contact__info-icon" />
          <h3 class="contact__info-heading">
            {{ $t('email.heading') }}
          </h3>
          <p
            class="contact__info-details"
            v-html="resolveLineBreakOfPunctuation($t('email.subheading'))"
          />
          <p class="contact__info-details">
            <LinkComponent
              :href="`mailto:${getContactEmailAddress($i18n.locale)}`"
              @click="trackContactClick(TRACK_TARGETS.email)"
            >
              {{ getContactEmailAddress($i18n.locale) }}
            </LinkComponent>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { track, TRACK_CATEGORIES, TRACK_CONTEXTS } from '@/services/tracking'
import { resolveLineBreakOfLastWord, resolveLineBreakOfPunctuation } from '@/helpers/string'
import { getContactEmailAddress } from '@/helpers/i18n'
import Button, { BUTTON_STYLES } from '@/components/Form/Button.vue'
import IconPhoneCall from '@/components/_icons/IconPhoneCall.svg'
import IconSpeechBubble from '@/components/_icons/IconSpeechBubble.svg'
import IconMail from '@/components/_icons/IconMail.svg'
import LinkComponent from '@/components/Link/Link.vue'

const TRACK_TARGETS = {
  email: 'email',
  phone: 'phone',
}

export default {
  name: 'Contact',
  components: {
    Button,
    IconMail,
    IconPhoneCall,
    IconSpeechBubble,
    LinkComponent,
  },

  data() {
    return {
      BUTTON_STYLES,
      getContactEmailAddress,
      resolveLineBreakOfLastWord,
      resolveLineBreakOfPunctuation,
      TRACK_TARGETS,
    }
  },

  computed: {
    ...mapState('page', ['isVisible']),
  },

  mounted() {
    this.showPage()
  },

  methods: {
    ...mapActions({
      showPage: 'page/show',
      addFlashErrorMessage: 'flash/addErrorMessage',
    }),

    openLiveChat() {
      if (window.$chatwoot) window.$chatwoot.toggle('open')
    },

    trackContactClick(target) {
      track(TRACK_CATEGORIES.contact, TRACK_CONTEXTS.contactPage, { target })
    },
  },
}
</script>

<style lang="scss" scoped>
$border-color: $color-light-grey-2;
$padding-x: 40px;
$padding-x-tiny: 25px;

.contact {
  width: 100%;

  &__hero {
    position: relative;
    // alternative: https://images.pexels.com/photos/137219/pexels-photo-137219.jpeg
    background-image: url('~@/components/Contact/contact-hero.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    height: 300px;

    @include min-screen($mq-medium) {
      height: 400px;
    }

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background-color: transparentize($color-red-bg, 0.3);
      z-index: 1;
    }
  }

  &__hero-title {
    position: absolute;
    top: calc(50% + #{$site-header-height-small / 2});
    left: 0;
    width: 100%;
    padding: 0 $padding-x-tiny;
    transform: translateY(-50%);
    font-size: 40px;
    font-weight: bold;
    line-height: 1.2;
    color: $color-white;
    text-transform: uppercase;
    text-align: center;
    z-index: 2;

    @include min-screen($mq-tiny) {
      padding: 0 $padding-x;
    }

    @include min-screen($mq-medium) {
      top: auto;
      bottom: 0;
      transform: translateY(0);
      font-size: 50px;
      line-height: 0.85;
    }

    @include min-screen($mq-large) {
      font-size: 60px;
    }
  }

  &__headline {
    width: 580px;
    max-width: 100%;
    margin: 50px auto 60px;
    padding: 0 $padding-x-tiny;
    font-size: $font-size-large;
    color: $color-text-light;
    text-align: center;

    @include min-screen($mq-tiny) {
      padding: 0 $padding-x;
    }

    @include min-screen($mq-medium) {
      margin: 50px auto 100px;
    }
  }

  &__info-table-wrap {
    margin: 0 $padding-x-tiny 120px;

    @include min-screen($mq-tiny) {
      margin-left: $padding-x;
      margin-right: $padding-x;
    }

    @include min-screen($mq-medium) {
      margin: 0 40px 160px;
    }
  }

  &__info-table {
    display: flex;
    flex-wrap: wrap;
    max-width: 600px;
    margin: 0 auto;
    border: 1px solid $border-color;
    border-radius: $border-radius;

    @include min-screen($mq-large) {
      flex-wrap: nowrap;
      max-width: 1200px;
    }
  }

  &__info-block {
    width: 100%;
    padding: 40px 30px;
    text-align: center;
    border-width: 0;
    border-style: solid;
    border-color: $border-color;

    & ~ & {
      border-top-width: 1px;
    }

    @include min-screen($mq-large) {
      flex: 1;
      width: 33.33%;

      & ~ & {
        border-top-width: 0;
        border-left-width: 1px;
      }
    }
  }

  &__info-icon {
    width: 48px;
    height: 48px;
    vertical-align: top;

    /deep/ path {
      stroke: $color-text-light;
      stroke-width: 1;
    }
  }

  &__info-heading {
    margin: 20px 0 25px;
    font-weight: bold;
  }

  &__info-details {
    margin-top: 12px;
    margin-bottom: 10px;
  }

  &__info-button {
    min-width: 120px;
  }

  &__info-button-wrap {
    margin-top: 20px;
  }

  &__info-after-button {
    margin-top: 35px;
  }
}
</style>

<i18n src="@/locales/contact.json" />
